import React from "react";

import { Box, IconButton, useTheme } from "@mui/material";

import { useThemeManager } from "src/platform/hooks/useThemeManager";

import {
  AuthFooter,
  AuthGraphicSection,
  AuthPageRoot,
  ContentWrapper,
  FormArea,
  FormCard,
  FormPanel,
  Subtitle,
  Tagline,
  TrustBar,
  Wordmark,
} from "./Layout.styled";

interface LayoutProps {
  Component: React.FC;
  showGraphic?: boolean;
}

const BrandPanel: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { toggleColorMode } = useThemeManager();

  return (
    <AuthGraphicSection>
      {/* A21: theme toggle, top-right of brand panel */}
      <IconButton
        onClick={toggleColorMode}
        aria-label="Switch theme"
        title="Switch theme"
        sx={{
          position: "absolute",
          top: 20,
          right: 20,
          color: "text.secondary",
          borderRadius: "8px",
          fontSize: "1.1rem",
          "&:hover": {
            color: "text.primary",
            bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          },
        }}
      >
        🌓
      </IconButton>

      <ContentWrapper>
        {/* Unified composition: R mark hub + pipeline satellites + stats */}
        <Box
          component="img"
          src={
            isDark
              ? "/brand/composition-dark.svg"
              : "/brand/composition-light.svg"
          }
          alt="Rusha — Engineering Control Plane"
          sx={{ maxWidth: 440, width: "100%" }}
        />

        <Box sx={{ mt: 2 }}>
          <Wordmark variant="h2">rusha</Wordmark>
          <Subtitle variant="caption">ENGINEERING CONTROL PLANE</Subtitle>
        </Box>

        <Tagline variant="body1">
          Ship infrastructure, not tickets.
          <br />
          Multi-cloud orchestration from commit to production.
        </Tagline>
      </ContentWrapper>

      {/* Trust bar */}
      <TrustBar>
        TRUSTED BY&nbsp; amband.co.ke &nbsp; oneworldanalytics.com &nbsp;
        tierraviva.ai
      </TrustBar>
    </AuthGraphicSection>
  );
};

export const Layout: React.FC<LayoutProps> = ({
  Component,
  showGraphic = true,
}) => {
  if (!showGraphic) {
    return (
      <AuthPageRoot>
        <FormPanel sx={{ flex: 1, borderRight: "none" }}>
          <FormArea>
            <FormCard>
              <Component />
            </FormCard>
          </FormArea>
          <AuthFooter>© 2026 Rusha — Privacy — Terms</AuthFooter>
        </FormPanel>
      </AuthPageRoot>
    );
  }

  return (
    <AuthPageRoot>
      {/* Left: Form panel */}
      <FormPanel>
        <FormArea>
          <FormCard>
            <Component />
          </FormCard>
        </FormArea>
        <AuthFooter>© 2026 Rusha — Privacy — Terms</AuthFooter>
      </FormPanel>

      {/* Right: Brand panel (stacks below form on mobile via AuthPageRoot) */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
        }}
      >
        <BrandPanel />
      </Box>
    </AuthPageRoot>
  );
};
